import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  C,
  DeliveryAddressDialog,
  DeliveryStatusDialog,
  IncomeHistoryDialog,
  TransferDialog,
} from "@/components/modal/home";
import { MyAgentsPageHeader } from "@/components/home/MyAgentsSection/MyAgentsPageHeader";
import { MyAgentsSectionBody } from "@/components/home/MyAgentsSection";
import {
  computeMyAgentsStats,
  filterMyAgents,
  type MyAgentsFilter,
} from "@/components/home/MyAgentsSection/myAgentsPageUtils";
import { useGetMyFortAgents } from "@/hooks/api/useGetMyFortAgents";
import { useUserStore } from "@/store/useUserStore";
import { MyFortAgentItem } from "@/api/types";

export default function MyAgents() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userInfo } = useUserStore();
  const { data: myAgents, isLoading: myAgentsLoading } = useGetMyFortAgents();

  const [addressOpen, setAddressOpen] = useState(false);
  const [addressAgent, setAddressAgent] = useState<MyFortAgentItem | null>(
    null
  );
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferAgent, setTransferAgent] = useState<MyFortAgentItem | null>(
    null
  );
  const [deliveryStatusOpen, setDeliveryStatusOpen] = useState(false);
  const [deliveryStatusAgent, setDeliveryStatusAgent] =
    useState<MyFortAgentItem | null>(null);
  const [incomeHistoryOpen, setIncomeHistoryOpen] = useState(false);
  const [incomeHistoryAgentId, setIncomeHistoryAgentId] = useState(0);
  const [filter, setFilter] = useState<MyAgentsFilter>("all");

  const agentList = myAgents?.agents ?? [];
  const stats = useMemo(() => computeMyAgentsStats(agentList), [agentList]);
  const filteredAgents = useMemo(
    () => filterMyAgents(agentList, filter),
    [agentList, filter]
  );

  return (
    <div className="pt-top-header-relaxed pb-24 px-4 max-w-lg mx-auto page-enter">
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all hover:opacity-80"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${C.border}`,
            color: C.muted,
          }}
          onClick={() => navigate(-1)}
          aria-label={t("topHeader.backToHome")}
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <h1 className="font-bold text-lg truncate">
            {t("homePage.myAgents.title")}
          </h1>
          {userInfo?.address && stats.total > 0 && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-bold shrink-0"
              style={{ background: `${C.primary}15`, color: C.primary }}
            >
              {t("myAgentsPage.ownedCount", { count: stats.total })}
            </span>
          )}
        </div>
      </div>

      {!myAgentsLoading && agentList.length > 0 && (
        <MyAgentsPageHeader
          filter={filter}
          onFilterChange={setFilter}
          total={stats.total}
          activatedCount={stats.activatedCount}
          pendingCount={stats.pendingCount}
          totalInvestment={stats.totalInvestment}
        />
      )}

      {!myAgentsLoading &&
      agentList.length > 0 &&
      filteredAgents.length === 0 ? (
        <div
          className="rounded-2xl text-center py-12 px-4"
          style={{
            background: "rgba(255,255,255,0.02)",
            border: `1px solid ${C.border}`,
          }}
        >
          <p className="text-sm font-medium" style={{ color: C.dim }}>
            {t("myAgentsPage.filterEmpty")}
          </p>
        </div>
      ) : (
        <MyAgentsSectionBody
          myAgentsLoading={myAgentsLoading}
          agents={filteredAgents}
          allAgents={agentList}
          onAddress={agent => {
            setAddressAgent(agent);
            setAddressOpen(true);
          }}
          onDeliveryStatus={agent => {
            setDeliveryStatusAgent(agent);
            setDeliveryStatusOpen(true);
          }}
          onTransfer={agent => {
            setTransferAgent(agent);
            setTransferOpen(true);
          }}
          onIncomeHistory={agent => {
            setIncomeHistoryAgentId(agent.agentId);
            setIncomeHistoryOpen(true);
          }}
        />
      )}

      <DeliveryAddressDialog
        open={addressOpen}
        agent={addressAgent}
        onClose={() => {
          setAddressOpen(false);
          setAddressAgent(null);
        }}
      />
      <DeliveryStatusDialog
        open={deliveryStatusOpen}
        agent={deliveryStatusAgent}
        onClose={() => {
          setDeliveryStatusOpen(false);
          setDeliveryStatusAgent(null);
        }}
      />
      {transferAgent != null && (
        <TransferDialog
          open={transferOpen}
          onClose={() => {
            setTransferOpen(false);
            setTransferAgent(null);
          }}
          agent={transferAgent}
        />
      )}
      <IncomeHistoryDialog
        open={incomeHistoryOpen}
        agentId={incomeHistoryAgentId}
        onClose={() => {
          setIncomeHistoryOpen(false);
          setIncomeHistoryAgentId(0);
        }}
      />
    </div>
  );
}
